import { useEffect, useId, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { formatBytes } from '@/lib/format-bytes'
import { useAnnounce } from '@/shared/providers/announcer-provider'
import { Button } from '@/ui/button'
import { FormField } from '@/ui/form-field'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { Switch } from '@/ui/switch'
import type {
  ContentUploadPolicyResult,
  DefineUploadLimit,
  UploadLimitDefinition,
} from '../../api/content-upload-policy-api'
import { isDefinitePolicyCreateRejection } from '../../lib/policy-authoring'
import { useCanEditSettings } from '../../lib/setting-queries'
import {
  uploadLimitErrorMessage,
  useContentUploadPolicyResult,
  useSetUploadLimit,
  useUploadLimitDefinition,
} from './upload-limit-queries'

const maximumUploadBytes = 10 * 1024 * 1024

const uploadLimitSchema = z.object({
  bytes: z.string().refine((value) => /^\d+$/.test(value)
    && Number(value) >= 1 && Number(value) <= maximumUploadBytes,
  'Enter a whole number from 1 through 10485760 bytes.'),
})

type UploadLimitValues = z.infer<typeof uploadLimitSchema>

export function UploadLimitSection({ entityId }: { entityId: string }) {
  const titleId = useId()
  const canEdit = useCanEditSettings(entityId)
  const definition = useUploadLimitDefinition(entityId)
  const result = useContentUploadPolicyResult(entityId)
  const [reloadVersion, setReloadVersion] = useState(0)
  async function reload() {
    const outcome = await definition.refetch()
    if (outcome.isSuccess) setReloadVersion((version) => version + 1)
  }

  return (
    <section className="flex flex-col gap-4" aria-labelledby={titleId}>
      <header className="flex flex-col gap-1">
        <h2 id={titleId} className="text-foreground text-base font-medium">Upload limit</h2>
        <p className="text-muted-foreground text-sm">
          The largest file people can upload to Content here. Subentities use this limit unless they
          set their own.
        </p>
      </header>
      {result.isError && (
        <RetryNotice message="The current upload limit is unavailable." buttonLabel="Retry loading the current limit"
          onRetry={() => void result.refetch()} />
      )}
      {definition.isError && (
        <RetryNotice message="Couldn’t load the upload limit." buttonLabel="Retry loading the upload limit"
          onRetry={() => void reload()} />
      )}
      {definition.isPending && <LoadingNotice message="Loading the upload limit…" />}
      {definition.data !== undefined && (
        <UploadLimitEditor key={reloadVersion} entityId={entityId} own={definition.data}
          result={result.data} canEdit={canEdit} focusOnMount={reloadVersion > 0} onReload={() => void reload()} />
      )}
    </section>
  )
}

function UploadLimitEditor({ entityId, own: initialOwn, result, canEdit, focusOnMount, onReload }: {
  entityId: string
  own: UploadLimitDefinition | null
  result: ContentUploadPolicyResult | undefined
  canEdit: boolean
  focusOnMount: boolean
  onReload: () => void
}) {
  const switchId = useId()
  const switchRef = useRef<HTMLElement>(null)
  const [own, setOwn] = useState(initialOwn)
  const [define, setDefine] = useState(own !== null)
  const [operationId, setOperationId] = useState(() => crypto.randomUUID())
  const [attemptedCreate, setAttemptedCreate] = useState<DefineUploadLimit | null>(null)
  const [uncertainCreate, setUncertainCreate] = useState(false)
  const save = useSetUploadLimit(entityId)
  const announce = useAnnounce()
  const { control, handleSubmit, reset, formState } = useForm<UploadLimitValues>({
    resolver: zodResolver(uploadLimitSchema),
    defaultValues: { bytes: own === null ? '' : String(own.value) },
  })
  const pending = save.isPending
  const locked = pending || uncertainCreate || !canEdit
  const changedMode = define !== (own !== null)
  const expectedDefinition = own === null ? null : { id: own.id, revision: own.revision }
  const hintId = `${switchId}-hint`
  const inProgress = result?.refreshStatus === 'Pending' || result?.refreshStatus === 'Running'
  useEffect(() => {
    if (focusOnMount) switchRef.current?.focus()
  }, [focusOnMount])
  const failed = result !== undefined && !inProgress
    && (result.selectionStatus === 'NeverEvaluated' || result.refreshStatus === 'Failed' || result.refreshStatus === 'Unknown')

  function inheritedDescription() {
    if (own) return 'Save to remove this entity’s limit and use the limit from above.'
    if (result?.isDefault) return 'Using the 10 MiB default.'
    if (result?.value != null) return `Inheriting ${formatBytes(result.value)} (${result.value.toLocaleString()} bytes) from above.`
    return 'Inheriting the limit from above.'
  }

  function submit({ bytes }: UploadLimitValues) {
    if (pending || !canEdit) return
    const write: DefineUploadLimit = attemptedCreate ?? {
      mode: 'Define',
      expectedDefinition,
      operationId,
      value: Number(bytes),
    }
    if (own === null) setAttemptedCreate(write)
    save.mutate(write, {
      onSuccess: ({ mutation }) => {
        if (mutation) setOwn({ id: mutation.id, revision: mutation.revision, value: write.value })
        reset({ bytes: String(write.value) })
        setAttemptedCreate(null)
        setOperationId(crypto.randomUUID())
        setUncertainCreate(false)
        announce('Upload limit saved.')
      },
      onError: (failure) => {
        if (own !== null) return
        if (!uncertainCreate && isDefinitePolicyCreateRejection(failure, 'CONTENT_UPLOAD_POLICY')) {
          setAttemptedCreate(null)
          return
        }
        setUncertainCreate(true)
      },
    })
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={(event) => {
      event.preventDefault()
      if (!canEdit || pending) return
      if (!define) {
        save.mutate({ mode: 'Inherit', expectedDefinition }, { onSuccess: () => {
          setOwn(null)
          announce('Upload limit removed. This entity now uses the limit from above.')
        } })
        return
      }
      void handleSubmit(submit)(event)
    }}>
      <div className="flex flex-col gap-2">
        <label htmlFor={switchId} className="flex items-center gap-3 text-sm font-medium">
          <Switch ref={switchRef} id={switchId} checked={define} disabled={locked} aria-describedby={hintId} onCheckedChange={(next) => {
            if (next && own === null) reset({ bytes: result?.value == null ? '' : String(result.value) })
            setDefine(next)
          }} />
          Set an upload limit for this entity
        </label>
        <p id={hintId} className="text-muted-foreground text-xs">
          {define ? 'Files larger than this are rejected here and at subentities without their own limit.'
            : inheritedDescription()}
          {!canEdit && ' You do not have permission to change the upload limit.'}
        </p>
      </div>
      {define && (
        <FormField control={control} name="bytes" disabled={locked}
          spec={{ label: 'Maximum upload size (bytes)', hint: '1 to 10,485,760 bytes (10 MiB).' }} />
      )}
      <p role="status" className={inProgress ? 'text-muted-foreground text-xs' : 'sr-only'}>
        {inProgress && (result?.selectionStatus === 'Evaluated'
          ? 'Applying the limit. Uploads may use a different limit until it finishes.'
          : 'Applying the limit. Uploads are unavailable here until it finishes.')}
      </p>
      {failed && (
        <p role="alert" className="text-destructive text-xs">
          The limit couldn’t be applied. Uploads are unavailable here until it is.
        </p>
      )}
      {uncertainCreate && (
        <p role="status" className="text-muted-foreground text-xs">
          The save may have succeeded. Retry the same limit to confirm, or reload the latest setting.
        </p>
      )}
      {save.isError && <p role="alert" className="text-destructive text-xs">{uploadLimitErrorMessage(save.error)}</p>}
      {canEdit && (
        <div className="flex justify-end gap-2">
          {save.isError && <Button type="button" variant="outline" disabled={pending} onClick={onReload}>Reload latest setting</Button>}
          <Button type="submit" disabled={pending || (!define && own === null) || (!changedMode && !formState.isDirty && !uncertainCreate)}>
            {pending ? 'Saving…' : uncertainCreate ? 'Retry same limit' : 'Save changes'}
          </Button>
        </div>
      )}
    </form>
  )
}
